import type { DocumentByPathResult } from '../document-context';

import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageContentRoot } from '#components/page-content-root/page-content-root';
import { useMediaMaxWidth } from '#hooks/use-media-max-width';
import { useAppConfig } from '#providers/app-config-provider';
import { usePageLayout } from '#providers/page-layout-provider';
import { useCurrentUser } from '#providers/user-provider';
import {
  documentHistoryHref,
  documentViewHref,
} from '#utils/document-app-path';

import { DocumentChatComposerProvider } from '../document-chat-composer-context';
import { DocumentProvider } from '../document-context';

import { DocumentChatFooter } from './document-chat-footer';
import { DOCUMENT_EDITOR_MOBILE_MAX_PX } from './document-editor-layout';
import { ViewDocumentHeader } from './view-document-header';
import { ViewDocumentViewer } from './view-document-viewer';

interface ViewDocumentProps {
  /** Document from parent query (Document or DocumentNotFound). */
  document: DocumentByPathResult;
  /** Canonical path (document path when found, else requested path from URL). */
  path: string;
  refetch: () => Promise<unknown>;
  lastServerEditTimestampRef: RefObject<number>;
}

/** Part of child path after parent path (for display). */
export function ViewDocument({
  document,
  path,
  refetch,
  lastServerEditTimestampRef,
}: ViewDocumentProps) {
  const navigate = useNavigate();
  const { config, recordRecentlyOpenedDocument } = useAppConfig();
  const user = useCurrentUser();

  const documentId =
    document?.__typename === 'Document' ? document.id : undefined;
  const displayPath =
    document?.__typename === 'Document'
      ? document.path
      : document?.__typename === 'DocumentNotFound'
        ? (document.requestedPath ?? path)
        : path;

  const [isCreateSubpageOpen, setIsCreateSubpageOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);

  const isMobileEditorLayout = useMediaMaxWidth(DOCUMENT_EDITOR_MOBILE_MAX_PX);
  const [composerFooterVisible, setComposerFooterVisible] = useState(
    () => !isMobileEditorLayout,
  );

  useEffect(() => {
    if (isMobileEditorLayout) {
      setComposerFooterVisible(false);
    } else {
      setComposerFooterVisible(true);
    }
  }, [isMobileEditorLayout]);

  const toggleComposerFooter = useCallback(() => {
    setComposerFooterVisible((v) => !v);
  }, []);

  const { registerContentSidebarCloseHandler, setContentSidebarDismissed } =
    usePageLayout();

  useEffect(() => {
    registerContentSidebarCloseHandler(() => {
      if (isMobileEditorLayout) {
        setComposerFooterVisible(false);
      } else {
        setContentSidebarDismissed(true);
      }
    });
    return () => registerContentSidebarCloseHandler(null);
  }, [
    isMobileEditorLayout,
    registerContentSidebarCloseHandler,
    setContentSidebarDismissed,
  ]);

  useEffect(() => {
    if (documentId) {
      recordRecentlyOpenedDocument(documentId, path);
    }
  }, [documentId, path, recordRecentlyOpenedDocument]);

  const onNavigateToDocument = useCallback(
    (docPath: string) => {
      navigate(documentViewHref(docPath));
    },
    [navigate],
  );

  const openCreateSubpagePrompt = useCallback(() => {
    setIsCreateSubpageOpen(true);
  }, []);

  return (
    <PageContentRoot flex={1} minH={0} display="flex" flexDirection="column">
      <DocumentProvider
        document={document}
        path={path}
        refetch={refetch}
        lastServerEditTimestampRef={lastServerEditTimestampRef}
      >
        <DocumentChatComposerProvider>
          <ViewDocumentHeader
            onNavigateToHistory={() =>
              navigate(documentHistoryHref(displayPath))
            }
            isCreateSubpageOpen={isCreateSubpageOpen}
            onCreateSubpageOpenChange={setIsCreateSubpageOpen}
            isMobileEditorLayout={isMobileEditorLayout}
            composerFooterVisible={composerFooterVisible}
            onComposerFooterToggle={toggleComposerFooter}
          />

          <Box width="100%" mx="auto" flexShrink={0}>
            <ViewDocumentViewer
              apiHost={config.apiHost}
              userEmail={user.email}
              onCreateSubpage={openCreateSubpagePrompt}
              onNavigateToDocument={onNavigateToDocument}
              isRenameOpen={isRenameOpen}
              onRenameOpenChange={setIsRenameOpen}
            />
          </Box>
          <DocumentChatFooter active={composerFooterVisible} />
        </DocumentChatComposerProvider>
      </DocumentProvider>
    </PageContentRoot>
  );
}
